/**
 * FrostedWaterDispenserModal — Apple Home 風格毛玻璃 飲水機 控制面板
 *
 * Cyan/Aqua tint — Dynamic 1~3 outlet temperature cards
 * Water Levels, Child Lock, Dynamic Filter Status with purchase links
 *
 * 完全自包含：parent 只需傳 deviceSN + credentials
 */
import { useState, useCallback, useRef, useEffect, useMemo } from 'react'
import type { DeviceModalTemplateProps, ModalSectionMeta } from '../types/deviceModalTemplate'
import { executeCommandWithAuth, configureDeviceNicknameWithAuth } from '../services/turingMcpApi'
import './FrostedWaterDispenserModal.css'

export const WATER_DISPENSER_FROSTED_SECTIONS: ModalSectionMeta[] = [
  { id: '_modal', label: 'Modal Background', icon: 'smartphone', slots: [] },
  { id: 'temps', label: 'Temperature Cards', icon: 'thermostat', slots: [
    { id: 'temp-card', label: 'Temp Card', editableProps: ['background', 'borderRadius', 'padding'] },
    { id: 'temp-value', label: 'Temp Value', editableProps: ['fontSize', 'color', 'fontWeight'] },
    { id: 'temp-label', label: 'Temp Label', editableProps: ['fontSize', 'color', 'letterSpacing'] },
  ]},
  { id: 'waterLevel', label: 'Water Level', icon: 'water_drop', slots: [
    { id: 'wl-title', label: 'Section Title', editableProps: ['fontSize', 'color', 'letterSpacing'] },
    { id: 'wl-bar', label: 'Level Bar', editableProps: ['background', 'borderRadius', 'height'] },
  ]},
  { id: 'childLock', label: 'Child Lock', icon: 'lock', slots: [
    { id: 'lock-label', label: 'Lock Label', editableProps: ['fontSize', 'color'] },
    { id: 'lock-toggle', label: 'Toggle Switch', editableProps: ['background', 'borderRadius'] },
  ]},
  { id: 'filters', label: 'Filter Status', icon: 'filter_alt', slots: [
    { id: 'filter-name', label: 'Filter Name', editableProps: ['fontSize', 'color'] },
    { id: 'filter-pct', label: 'Percentage', editableProps: ['fontSize', 'color', 'fontWeight'] },
    { id: 'filter-bar', label: 'Progress Bar', editableProps: ['background', 'borderRadius', 'height'] },
    { id: 'filter-buy', label: 'Purchase Button', editableProps: ['background', 'color', 'borderRadius', 'border'] },
  ]},
]

const DEFAULT_SECTION_ORDER = WATER_DISPENSER_FROSTED_SECTIONS.map((s) => s.id)

// ---------------------------------------------------------------------------
// Utility helpers
// ---------------------------------------------------------------------------
function getAttr(state: Record<string, unknown> | null, ...keys: string[]): unknown {
  if (!state) return undefined
  for (const key of keys) { if (key in state && state[key] !== undefined && state[key] !== null) return state[key] }
  return undefined
}
function numAttr(state: Record<string, unknown> | null, ...keys: string[]): number | null {
  let v = getAttr(state, ...keys)
  if (v === undefined || v === null) return null
  if (Array.isArray(v)) v = v[0]
  if (v === undefined || v === null) return null
  const n = Number(v); return isNaN(n) ? null : n
}
function strAttr(state: Record<string, unknown> | null, ...keys: string[]): string {
  const v = getAttr(state, ...keys); return v != null ? String(v) : ''
}

// ---------------------------------------------------------------------------
// Temperature outlet definition types
// ---------------------------------------------------------------------------
type TempOutlet = {
  id: string
  label: string
  icon: string
  colorClass: string
  tempAttrKeys: string[]
  targetAttrKeys: string[]
  levelAttrKeys: string[]
  setCmd: string
  min: number
  max: number
  step: number
  pickerTitle: string
}

const OUTLET_DEFS: TempOutlet[] = [
  {
    id: 'ice', label: 'Ice', icon: 'ac_unit', colorClass: 'ice',
    tempAttrKeys: ['iceTemp', 'icewaterTemperature', 'coldTemp'],
    targetAttrKeys: ['iceTargetTemp', 'icewaterTargetTemperature', 'coldTargetTemp'],
    levelAttrKeys: ['iceTankLevel', 'icewaterLevel', 'coldTankLevel'],
    setCmd: 'setIceTemp', min: 3, max: 10, step: 1, pickerTitle: '冰水目標溫度',
  },
  {
    id: 'warm', label: 'Warm', icon: 'coffee', colorClass: 'warm',
    tempAttrKeys: ['warmTemp', 'warmwaterTemperature'],
    targetAttrKeys: ['warmTargetTemp', 'warmwaterTargetTemperature'],
    levelAttrKeys: ['warmTankLevel', 'warmwaterLevel'],
    setCmd: 'setWarmTemp', min: 35, max: 55, step: 1, pickerTitle: '溫水目標溫度',
  },
  {
    id: 'hot', label: 'Hot', icon: 'local_fire_department', colorClass: 'hot',
    tempAttrKeys: ['hotTemp', 'hotwaterTemperature'],
    targetAttrKeys: ['hotTargetTemp', 'hotwaterTargetTemperature'],
    levelAttrKeys: ['hotTankLevel', 'hotwaterLevel'],
    setCmd: 'setHotTemp', min: 80, max: 100, step: 1, pickerTitle: '熱水目標溫度',
  },
]

// Single-outlet fallback
const SINGLE_OUTLET: TempOutlet = {
  id: 'water', label: 'Water Temp', icon: 'water_drop', colorClass: 'warm',
  tempAttrKeys: ['waterTemp', 'waterTemperature', 'temperature'],
  targetAttrKeys: ['waterTargetTemp', 'targetTemperature'],
  levelAttrKeys: ['waterLevel', 'tankLevel'],
  setCmd: 'setTemperature', min: 5, max: 95, step: 1, pickerTitle: '目標溫度',
}

// ---------------------------------------------------------------------------
// Filter definition helpers
// ---------------------------------------------------------------------------
type FilterDef = { id: string; name: string; attrKeys: string[] }
const FILTER_DEFS: FilterDef[] = [
  { id: 'pp', name: 'PP 濾芯', attrKeys: ['filterLifePP', 'ppFilterLife'] },
  { id: 'carbon', name: '活性碳濾芯', attrKeys: ['filterLifeCarbon', 'carbonFilterLife'] },
  { id: 'ro', name: 'RO 膜濾芯', attrKeys: ['filterLifeRO', 'roFilterLife'] },
  { id: 'generic', name: '濾芯', attrKeys: ['filterLife'] },
]

function filterStatus(pct: number): { cls: string; fillCls: string } {
  if (pct >= 50) return { cls: 'wdisp-frosted__filter-pct--good', fillCls: 'wdisp-frosted__filter-fill--good' }
  if (pct >= 30) return { cls: 'wdisp-frosted__filter-pct--warn', fillCls: 'wdisp-frosted__filter-fill--warn' }
  return { cls: 'wdisp-frosted__filter-pct--low', fillCls: 'wdisp-frosted__filter-fill--low' }
}

function estimateDaysRemaining(pct: number): string {
  const days = Math.round(pct * 1.5)
  return `~${days} 天剩餘`
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
export function FrostedWaterDispenserModal({
  device, deviceState, onCommand, onClose, isEditorMode, cssOverrides,
  sectionOrder, sectionVisible, onSectionContextMenu, highlightedSection, credentials,
}: DeviceModalTemplateProps) {

  const handleContextMenu = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    if (!isEditorMode || !onSectionContextMenu) return
    const sectionEl = (event.target as HTMLElement)?.closest('[data-modal-section]') as HTMLElement | null
    if (!sectionEl) return
    event.preventDefault(); event.stopPropagation()
    const sectionId = sectionEl.dataset.modalSection || ''
    if (sectionId) onSectionContextMenu(sectionId, { clientX: event.clientX, clientY: event.clientY })
  }, [isEditorMode, onSectionContextMenu])

  const orderedSections = useMemo(() => {
    const order = sectionOrder?.length ? sectionOrder : DEFAULT_SECTION_ORDER
    return [...order, ...DEFAULT_SECTION_ORDER.filter((id) => !order.includes(id))]
  }, [sectionOrder])
  const isSectionVisible = useCallback((id: string) => sectionVisible?.[id] !== false, [sectionVisible])

  // ── Device state extraction ──
  const isOn = Boolean(getAttr(deviceState, 'on'))
  const childLock = Boolean(getAttr(deviceState, 'childLock', 'lockState', 'isChildLocked'))

  // Determine which outlets the device supports
  const activeOutlets = useMemo(() => {
    const found: TempOutlet[] = []
    for (const def of OUTLET_DEFS) {
      const hasTemp = def.tempAttrKeys.some(k => deviceState && k in deviceState && deviceState[k] != null)
      if (hasTemp) found.push(def)
    }
    if (found.length === 0) {
      // Check for single-outlet water dispenser
      const hasSingle = SINGLE_OUTLET.tempAttrKeys.some(k => deviceState && k in deviceState && deviceState[k] != null)
      if (hasSingle) return [SINGLE_OUTLET]
      // Fallback: show all 3 with placeholder data
      return OUTLET_DEFS
    }
    return found
  }, [deviceState])

  const isSingleOutlet = activeOutlets.length === 1 && activeOutlets[0].id === 'water'

  // Determine which filters exist
  const activeFilters = useMemo(() => {
    const found: Array<FilterDef & { pct: number }> = []
    for (const def of FILTER_DEFS) {
      const val = numAttr(deviceState, ...def.attrKeys)
      if (val !== null) found.push({ ...def, pct: val })
    }
    return found
  }, [deviceState])

  // ── Settings overlay ──
  const [showSettings, setShowSettings] = useState(false)
  const deviceInfoItems = useMemo(() => {
    const items: Array<{ label: string; value: string; action?: 'copy' }> = []
    items.push({ label: 'DEVICE TYPE', value: String(device.type || 'Unknown') })
    if (device.ip) items.push({ label: 'IP ADDRESS', value: String(device.ip) })
    if (device.fwVersion || device.firmwareVersion) items.push({ label: 'FW VERSION', value: String(device.fwVersion || device.firmwareVersion) })
    if (device.rssi != null) items.push({ label: 'RSSI', value: String(device.rssi) })
    items.push({ label: 'DEVICE SN', value: device.sn, action: 'copy' })
    return items
  }, [device])
  const [copiedField, setCopiedField] = useState<string | null>(null)
  const copyToClipboard = useCallback((text: string, label: string) => {
    navigator.clipboard.writeText(text).catch(() => {}); setCopiedField(label); setTimeout(() => setCopiedField(null), 1000)
  }, [])

  // ── Rename ──
  const [isRenaming, setIsRenaming] = useState(false)
  const [renameValue, setRenameValue] = useState('')
  const [displayName, setDisplayName] = useState(device.name || device.sn)
  const renameInputRef = useRef<HTMLInputElement>(null)
  useEffect(() => { setDisplayName(device.name || device.sn) }, [device.name, device.sn])
  const startRename = useCallback(() => { setRenameValue(displayName); setIsRenaming(true); setTimeout(() => renameInputRef.current?.focus(), 50) }, [displayName])
  const submitRename = useCallback(async () => {
    const trimmed = renameValue.trim()
    if (!trimmed || trimmed === displayName || !credentials) { setIsRenaming(false); return }
    setDisplayName(trimmed); setIsRenaming(false)
    await configureDeviceNicknameWithAuth(credentials, device.sn, trimmed).catch(() => {})
  }, [renameValue, displayName, credentials, device.sn])

  // ── Pending state ──
  const [pendingPower, setPendingPower] = useState<boolean | null>(null)
  const [pendingLock, setPendingLock] = useState<boolean | null>(null)
  const displayPower = pendingPower ?? isOn
  const displayLock = pendingLock ?? childLock

  useEffect(() => { if (pendingPower !== null && pendingPower === isOn) setPendingPower(null) }, [isOn, pendingPower])
  useEffect(() => { if (pendingLock !== null && pendingLock === childLock) setPendingLock(null) }, [childLock, pendingLock])

  const execCmd = useCallback((cmd: string, params?: Record<string, unknown>) => {
    if (!credentials || !device.sn) return
    executeCommandWithAuth(credentials, [device.sn], cmd, params).catch(() => {})
    if (onCommand) onCommand(cmd, params)
  }, [credentials, device.sn, onCommand])

  const togglePower = useCallback(() => {
    if (isEditorMode) return; const next = !displayPower; setPendingPower(next); execCmd(next ? 'on' : 'off')
  }, [isEditorMode, displayPower, execCmd])

  const toggleChildLock = useCallback(() => {
    if (isEditorMode) return; const next = !displayLock; setPendingLock(next); execCmd('setChildLock', { lock: next })
  }, [isEditorMode, displayLock, execCmd])

  // ── Picker state ──
  const [pickerOpen, setPickerOpen] = useState(false)
  const [pickerOutlet, setPickerOutlet] = useState<TempOutlet | null>(null)
  const [pickerValue, setPickerValue] = useState(0)
  const pickerWheelRef = useRef<HTMLDivElement>(null)

  const openPicker = useCallback((outlet: TempOutlet) => {
    if (isEditorMode || !displayPower) return
    const currentTarget = numAttr(deviceState, ...outlet.targetAttrKeys) ?? numAttr(deviceState, ...outlet.tempAttrKeys) ?? outlet.min
    setPickerOutlet(outlet)
    setPickerValue(currentTarget)
    setPickerOpen(true)
  }, [isEditorMode, displayPower, deviceState])

  const confirmPicker = useCallback(() => {
    if (pickerOutlet) {
      execCmd(pickerOutlet.setCmd, { temperature: pickerValue })
    }
    setPickerOpen(false)
    setPickerOutlet(null)
  }, [pickerOutlet, pickerValue, execCmd])

  const closePicker = useCallback(() => { setPickerOpen(false); setPickerOutlet(null) }, [])

  const pickerItems = useMemo(() => {
    if (!pickerOutlet) return []
    const items: number[] = []
    for (let t = pickerOutlet.min; t <= pickerOutlet.max; t += pickerOutlet.step) items.push(t)
    return items
  }, [pickerOutlet])

  // Scroll picker to selected value when opened
  useEffect(() => {
    if (!pickerOpen || !pickerWheelRef.current || !pickerOutlet) return
    const idx = pickerItems.indexOf(pickerValue)
    if (idx >= 0) {
      setTimeout(() => {
        const wheel = pickerWheelRef.current
        if (wheel) wheel.scrollTop = idx * 40
      }, 60)
    }
  }, [pickerOpen, pickerOutlet, pickerValue, pickerItems])

  const handleHeat = useCallback(() => {
    if (isEditorMode) return
    execCmd('heat')
  }, [isEditorMode, execCmd])

  const stateClass = displayPower ? 'wdisp-frosted--on' : 'wdisp-frosted--off'
  const hl = (id: string) => highlightedSection === id ? ' section-highlighted' : ''

  // ── Section renderers ──
  const renderSection = (sectionId: string) => {
    if (!isSectionVisible(sectionId)) return null
    switch (sectionId) {
      case 'temps':
        return (
          <div key="temps" className={`wdisp-frosted__temps-grid${hl('temps')}`} data-modal-section="temps"
            data-highlighted={highlightedSection === 'temps' || undefined}
            style={isSingleOutlet ? { justifyContent: 'center' } : undefined}>
            {activeOutlets.map(outlet => {
              const temp = numAttr(deviceState, ...outlet.tempAttrKeys)
              const target = numAttr(deviceState, ...outlet.targetAttrKeys)
              const isHot = outlet.id === 'hot'
              return (
                <div key={outlet.id}
                  className={`wdisp-frosted__temp-card wdisp-frosted__temp-card--${outlet.colorClass}${isSingleOutlet ? ' wdisp-frosted__temp-card--single' : ''}`}>
                  <span className="wdisp-frosted__temp-card-icon material-symbols-outlined"
                    style={{ fontVariationSettings: "'FILL' 1" }}>{outlet.icon}</span>
                  <div className="wdisp-frosted__temp-card-label">{outlet.label}</div>
                  <div className="wdisp-frosted__temp-card-value" onClick={() => openPicker(outlet)}>
                    {displayPower ? (temp ?? '--') : '--'}
                    <span className="wdisp-frosted__temp-card-unit">{'\u00B0C'}</span>
                  </div>
                  {displayPower && target != null && (
                    <div className="wdisp-frosted__temp-card-target">
                      <span className="material-symbols-outlined" style={{ fontSize: 12 }}>tune</span>
                      {` 目標 ${target}\u00B0C`}
                    </div>
                  )}
                  {isSingleOutlet && !displayPower && (
                    <div className="wdisp-frosted__temp-card-sub">常溫水</div>
                  )}
                  {isHot && displayPower && (
                    <button className="wdisp-frosted__heat-btn" onClick={handleHeat}>
                      <span className="material-symbols-outlined" style={{ fontSize: 14, fontVariationSettings: "'FILL' 1" }}>whatshot</span>
                      {' 加熱'}
                    </button>
                  )}
                </div>
              )
            })}
          </div>
        )

      case 'waterLevel': {
        // Build water level rows from outlets that have level data
        const levelRows: Array<{ label: string; fillClass: string; pct: number; amount: string }> = []
        for (const outlet of activeOutlets) {
          const level = numAttr(deviceState, ...outlet.levelAttrKeys)
          if (level != null) {
            const fillSuffix = outlet.id === 'water' ? 'default' : outlet.colorClass
            levelRows.push({
              label: outlet.id === 'ice' ? 'Ice' : outlet.id === 'warm' ? '暖' : outlet.id === 'hot' ? 'Hot' : '水量',
              fillClass: `wdisp-frosted__wl-fill--${fillSuffix}`,
              pct: Math.max(0, Math.min(100, level)),
              amount: `${(level * 0.025).toFixed(1)} L`,
            })
          }
        }
        // Also check for generic waterLevel
        if (levelRows.length === 0) {
          const wl = numAttr(deviceState, 'waterLevel', 'tankLevel')
          if (wl != null) {
            levelRows.push({
              label: '水量', fillClass: 'wdisp-frosted__wl-fill--default',
              pct: Math.max(0, Math.min(100, wl)), amount: `${(wl * 0.025).toFixed(1)} L`,
            })
          }
        }
        if (levelRows.length === 0) return null
        return (
          <div key="waterLevel" className={`wdisp-frosted__water-levels${hl('waterLevel')}`}
            data-modal-section="waterLevel" data-highlighted={highlightedSection === 'waterLevel' || undefined}>
            <div className="wdisp-frosted__wl-title">
              <span className="material-symbols-outlined" style={{ fontVariationSettings: "'FILL' 1" }}>water_drop</span>
              {' Water Level'}
            </div>
            <div className="wdisp-frosted__wl-rows">
              {levelRows.map(row => (
                <div key={row.label} className="wdisp-frosted__wl-row">
                  <div className="wdisp-frosted__wl-label">{row.label}</div>
                  <div className="wdisp-frosted__wl-bar">
                    <div className={`wdisp-frosted__wl-fill ${row.fillClass}`} style={{ width: `${row.pct}%` }}/>
                  </div>
                  <div className="wdisp-frosted__wl-amount">{row.amount}</div>
                </div>
              ))}
            </div>
          </div>
        )
      }

      case 'childLock':
        return (
          <div key="childLock" className={`wdisp-frosted__controls${hl('childLock')}`}
            data-modal-section="childLock" data-highlighted={highlightedSection === 'childLock' || undefined}>
            <div className="wdisp-frosted__lock-row">
              <div className="wdisp-frosted__lock-left">
                <span className="wdisp-frosted__lock-icon material-symbols-outlined"
                  style={{ fontVariationSettings: "'FILL' 1" }}>lock</span>
                <div className="wdisp-frosted__lock-text">
                  <div className="wdisp-frosted__lock-label">Child Lock</div>
                  <div className="wdisp-frosted__lock-sub">{'\u9396\u5B9A\u71B1\u6C34\u51FA\u6C34'}</div>
                </div>
              </div>
              <button className={`wdisp-frosted__toggle${displayLock ? ' wdisp-frosted__toggle--active' : ''}`} onClick={toggleChildLock}>
                <div className="wdisp-frosted__toggle-knob"/>
              </button>
            </div>
          </div>
        )

      case 'filters': {
        if (activeFilters.length === 0) return null
        return (
          <div key="filters" className={`wdisp-frosted__filters${hl('filters')}`}
            data-modal-section="filters" data-highlighted={highlightedSection === 'filters' || undefined}>
            {activeFilters.map(f => {
              const st = filterStatus(f.pct)
              const isLow = f.pct < 30
              return (
                <div key={f.id} className="wdisp-frosted__filter-card">
                  <div className="wdisp-frosted__filter-header">
                    <div className="wdisp-frosted__filter-name">
                      <span className="material-symbols-outlined" style={{ fontVariationSettings: "'FILL' 1" }}>filter_alt</span>
                      {` ${f.name}`}
                    </div>
                    <div className={`wdisp-frosted__filter-pct ${st.cls}`}>{f.pct}%</div>
                  </div>
                  <div className="wdisp-frosted__filter-bar">
                    <div className={`wdisp-frosted__filter-fill ${st.fillCls}`} style={{ width: `${f.pct}%` }}/>
                  </div>
                  <div className="wdisp-frosted__filter-sub">
                    <span>{estimateDaysRemaining(f.pct)}{isLow ? ' \u26A0\uFE0F' : ''}</span>
                    <button className={`wdisp-frosted__filter-buy${isLow ? ' wdisp-frosted__filter-buy--urgent' : ''}`}
                      onClick={() => { /* Purchase link — delegated to parent or URL */ }}>
                      <span className="material-symbols-outlined" style={{ fontSize: 12, fontVariationSettings: "'FILL' 1" }}>shopping_cart</span>
                      {isLow ? '\u7ACB\u5373\u8CFC\u8CB7' : '\u8CFC\u8CB7'}
                    </button>
                  </div>
                </div>
              )
            })}
          </div>
        )
      }

      default: return null
    }
  }

  return (
    <div className={`wdisp-frosted ${stateClass}`} data-modal-template-id="water-dispenser-frosted" onContextMenu={handleContextMenu}>
      {cssOverrides && <style>{cssOverrides}</style>}
      <div className="wdisp-frosted__header" data-modal-section="_modal"
        data-highlighted={highlightedSection === '_modal' || undefined}>
        <button className="wdisp-frosted__back" onClick={onClose} aria-label="Back">{'\u2039'}</button>
        <span className="wdisp-frosted__name">{displayName}</span>
        <button className={`wdisp-frosted__power-btn${displayPower ? ' wdisp-frosted__power-btn--on' : ''}`} onClick={togglePower} aria-label="Power">
          <span className="material-symbols-outlined" style={{ fontSize: 20 }}>power_settings_new</span>
        </button>
      </div>

      {orderedSections.map(id => renderSection(id))}

      <button className="wdisp-frosted__settings-btn" onClick={() => setShowSettings(true)} aria-label="Settings">
        <span className="material-symbols-outlined" style={{ fontSize: 28 }}>settings</span>
      </button>

      {/* Settings overlay */}
      {showSettings && (
        <div className="wdisp-frosted__settings-overlay" onClick={() => setShowSettings(false)}>
          <div className="wdisp-frosted__settings-sheet" onClick={e => e.stopPropagation()}>
            <div className="wdisp-frosted__settings-header">
              <span className="wdisp-frosted__settings-title">{'\u8A2D\u5B9A'}</span>
              <button className="wdisp-frosted__settings-close" onClick={() => setShowSettings(false)}>{'\u2715'}</button>
            </div>
            <div className="wdisp-frosted__settings-section">
              <div className="wdisp-frosted__settings-section-title">{'\u88DD\u7F6E\u540D\u7A31'}</div>
              {isRenaming ? (
                <div className="wdisp-frosted__settings-row">
                  <input ref={renameInputRef} className="wdisp-frosted__rename-input" value={renameValue}
                    onChange={e => setRenameValue(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') submitRename(); if (e.key === 'Escape') setIsRenaming(false) }}
                    onBlur={submitRename} maxLength={40}/>
                </div>
              ) : (
                <div className="wdisp-frosted__settings-row">
                  <span className="wdisp-frosted__settings-value" style={{ textAlign: 'left', flex: 1 }}>{displayName}</span>
                  <button className="wdisp-frosted__settings-edit" onClick={startRename} title="Rename">
                    <span className="material-symbols-outlined" style={{ fontSize: 16 }}>edit</span>
                  </button>
                </div>
              )}
            </div>
            <div className="wdisp-frosted__settings-section">
              <div className="wdisp-frosted__settings-section-title">{'\u88DD\u7F6E\u8CC7\u8A0A'}</div>
              {deviceInfoItems.map(item => (
                <div key={item.label} className="wdisp-frosted__settings-row">
                  <span className="wdisp-frosted__settings-label">{item.label}</span>
                  <span className="wdisp-frosted__settings-value">{item.value}</span>
                  {item.action === 'copy' && (
                    <button className="wdisp-frosted__settings-copy" onClick={() => copyToClipboard(item.value, item.label)} title="Copy">
                      {'\uD83D\uDCCB'}{copiedField === item.label && <span className="wdisp-frosted__copied-toast">Copied</span>}
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Temperature Picker Popup */}
      {pickerOpen && pickerOutlet && (
        <div className="wdisp-frosted__picker-overlay" onClick={closePicker}>
          <div className="wdisp-frosted__picker" onClick={e => e.stopPropagation()}>
            <div className="wdisp-frosted__picker-header">
              <div className="wdisp-frosted__picker-title">{pickerOutlet.pickerTitle}</div>
              <button className="wdisp-frosted__picker-done" onClick={confirmPicker}>{'\u78BA\u5B9A'}</button>
            </div>
            <div className="wdisp-frosted__picker-wheel" ref={pickerWheelRef}>
              {/* Top padding for scroll centering */}
              <div style={{ height: 80 }}/>
              {pickerItems.map(t => (
                <div key={t}
                  className={`wdisp-frosted__picker-item${t === pickerValue ? ' wdisp-frosted__picker-item--selected' : ''}`}
                  onClick={() => setPickerValue(t)}>
                  {t}{'\u00B0C'}
                </div>
              ))}
              {/* Bottom padding for scroll centering */}
              <div style={{ height: 80 }}/>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
